import React, {useState} from "react";
import { Box, Button } from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import BackspaceIcon from "@mui/icons-material/Backspace"; // Import backspace icon
import { cobotColours } from "../theme/theme";
import { useDispatch } from "react-redux"; // Import useDispatch
import { setLogin } from "../state"; // Import setLogin action
import api from "../services/api"; // Import API service

/**
 * Check-in page component.
 * @returns {JSX.Element} The check-in page.
 */
const CheckinPage = () => {

    // State variables
    const colors = cobotColours();
    const [numbers, setNumbers] = useState([]); // State for entered numbers
    const { id } = useParams();
    const Navigate  = useNavigate();
    const dispatch = useDispatch(); // Initialize useDispatch

    // Event handlers
    const handleNumberClick = (number) => {
        // Validate number and limit input to 4 digits
        if (numbers.length < 4) {
            const updatedNumbers = [...numbers, number];
            setNumbers(updatedNumbers);
            if (updatedNumbers.length === 4) {
                handleConfirm(updatedNumbers);
            }
        }
    };

    const handleBackspace = () => {
        // Remove last entered number
        setNumbers(numbers.slice(0, -1));
    };

    const handleConfirm = async (pin) => {
        try {
            const data = await api.auth.validateEmployee(id, pin.join(""));

            if (data.success && data.token && data.user) {
                // Dispatch setLogin action with user and token
                dispatch(
                    setLogin({ 
                        user: data.user, 
                        token: data.token,
                        employeeId: data.employeeId // Assuming employeeId is returned and needed in state
                    })
                );

                if (data.isClockedIn) {
                    // Pass clockInTime in state to EmployeeClockedIn page
                    Navigate(`/EmployeeClockedIn/${id}`, { state: { clockInTime: data.clockInTime } });
                } else {
                    Navigate(`/ClockInPhoto/${id}`);
                }
            } else {
                setNumbers(""); // Clear PIN input on incorrect PIN or missing token/user
                // Optionally: show a more specific error to the user based on data.message
                if (data.message) {
                    alert(data.message); // Simple alert, consider a more polished notification
                }
            }

        } catch (error) {
            console.error("Error validating PIN:", error); // More specific error message
            setNumbers(""); // Clear PIN input on error
        }
    };


    return (
        <Box
            width={{ xs: "100vw", md: "65vw" }}
            height={{ xs: "50vh", md: "50vh" }}
        >
            {/* Display entered numbers */}
            <Box
                display="flex"
                justifyContent="center"
                margin="30px 0"
            >
                {[0, 1, 2, 3].map((index) => (
                    <Box
                        key={index}
                        width="30px"
                        height="30px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="20px"
                        margin="5px"
                        border="none"
                        sx={{
                            backgroundColor: colors.grey.lighter,
                            borderRadius: "90%",
                            fontWeight: "bold",
                            color: colors.text.black,
                        }}
                    >
                        {numbers[index] !== undefined ? "*" : ""}
                    </Box>
                ))}
            </Box>

            {/* Number input buttons */}
            <Box width="100%" display="flex" justifyContent="center">
                <Box display="flex" justifyContent="space-between" alignItems="center">

                    {/* Number input buttons grid */}
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(3, 80px)"
                        gridTemplateRows="repeat(4, 80px)"
                        gap="25px"
                        justifyContent="center"
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, "empty", 0, "back"].map((item, index) => (
                            <Button
                                key={index}
                                sx={{
                                    fontSize: item === "empty" ? "0px" : "25px", // Hide empty slot
                                    fontWeight: "900",
                                    margin: "5px",
                                    color: colors.text.black,
                                    backgroundColor: item === "empty" ? "transparent" : colors.grey.lighter,
                                    borderRadius: "50%",
                                    visibility: item === "empty" ? "hidden" : "visible",
                                }}
                                onClick={() => {
                                    if (typeof item === "number") handleNumberClick(item);
                                    if (item === "back") handleBackspace();
                                }}
                            >
                                {item === "back" ? <BackspaceIcon fontSize="medium" /> : item}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </Box>
            <Box display="flex" justifyContent="center">
                <Button 
                    variant="outlined"
                    color="secondary"
                    sx={{mt: 4, width: "200px", padding: "10px"}}
                    onClick={() => Navigate(`/`)} // Go back if needed
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );
};

export default CheckinPage;
